import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddEditAdUnitContext } from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { Schema } from '../schema'
import { UPDATE_AD_UNIT_UNFILLED_RECOVERY } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdateAdUnitUnfilledRecovery } from './types'

export const useUpdateAdUnitUnfilledRecovery = (): UseUpdateAdUnitUnfilledRecovery => {
  const { t } = useTranslation('features/adUnit')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditAdUnitContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_AD_UNIT_UNFILLED_RECOVERY,
    onCompleted: async ({ updateAdUnitUnfilledRecovery: { errors } }: Data) => {
      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['adUnit'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const update = ({ allowMultiformatEnabled, id, unfilledRecoveryEnabled }: Schema): void => {
    mutateAsync({
      input: {
        adUnitId: id,
        allowMultiformatEnabled: unfilledRecoveryEnabled ? allowMultiformatEnabled : false,
        unfilledRecoveryEnabled,
      },
    })
  }

  return { errors, update }
}
